import React, { useCallback, useMemo } from 'react';
import streamSaver from 'streamsaver';
import parse from 'url-parse';
import { ChunkedJSONSearch } from 'genomic-file-upload';

import { Region } from '../types';
import { PeakResult, DownloadProps, BatchResponse } from './types';

const url = parse(window.location.href);
streamSaver.mitm = `${url.protocol}//${url.host}/mitm.html`;

function bedLine(d: PeakResult): string {
    return (
        `${d.chrom}\t${d.chrom_start}\t${d.chrom_end}\t${d.experiment_accession}\t` +
        `${d.target}_${d.biosample}_${d.file_accession}\t1000\t.\t${d.p_value}\t${d.q_value}`
    );
}

const Download: React.FC<DownloadProps> = props => {
    const writer = useMemo(() => streamSaver.createWriteStream(`${props.name}.${props.format}`).getWriter(), [props]);

    const getReader = useCallback(
        async (search: Region[]) => {
            const variables = {
                assembly: props.assembly,
                range: search,
                assay: 'chip_seq',
                dataformat: props.format,
                target: props.factor,
                experiment_accession: props.accession,
            };
            const response = await fetch(props.streamPeaksService, {
                method: 'post',
                body: JSON.stringify(variables),
                headers: {
                    'Content-Type': 'application/json',
                    Accept: 'application/json',
                },
            });
            return response && response.body && response.body.getReader();
        },
        [props]
    );

    const getResults = useCallback((response: BatchResponse[]) => {
        let results: PeakResult[] = [];
        response.forEach(set => {
            results = results.concat(...set.peaks);
        });
        return results;
    }, []);

    const getProgress = useCallback((response: BatchResponse[]) => {
        return response.reduce((x, v) => x + v.progress, 0) * 100.0;
    }, []);

    const onBatchComplete = useCallback(
        (results: PeakResult[]) => {
            writer.write(new TextEncoder().encode(results.map(bedLine).join('\n')));
        },
        [writer]
    );

    const onComplete = useCallback(() => {
        writer
            .close()
            .then(props.onComplete)
            .catch(props.onComplete);
    }, [writer, props.onComplete]);

    const onError = useCallback(
        (error: any) => {
            writer
                .close()
                .then(() => props.onError(error))
                .catch(() => props.onError(error));
            props.onError(error);
        },
        [writer, props]
    );

    return (
        <ChunkedJSONSearch
            getProgress={getProgress}
            onBatchComplete={onBatchComplete}
            onComplete={onComplete}
            onError={onError}
            getReader={getReader}
            getResults={getResults}
            delimiter="endofbatch"
            searchTerms={props.regions}
        />
    );
};
export default Download;
